import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isNil } from '@semios/app-platform-common'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { DropdownSelectorFlowMeter } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorFlowMeter/DropdownSelectorFlowMeter'
import { FlowMeterSettingsTitleLink } from 'App/Map/PanelDetails/SectionTitleBars/FlowMeterTitleChildren/FlowMeterSettingsTitleLink'
import type { StackedChartSection } from 'components/StackedChart/types'
import type { SeriesOptionsType, TooltipFormatterContextObject } from 'highcharts'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TPoint } from 'stores/fieldAssetStore/helperTypes'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { getXDateFormat } from '../../_utils/getXDateFormat'
import { FlowMeterChartLegend } from './FlowMeterChartLegend'

const checkPermission = () =>
  selectedPropertyHasPermission({ permission: 'VIEW_IRRIGATION_FLOW_METER_DETAILS' })

export const flowMeterChart = ({
  data,
  points,
  selectedFieldAssets,
  updateData,
}: {
  data: routes.Values.Response
  points: TPoint[]
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  updateData: (pathToSet: string, dataToSet: unknown) => void
}): StackedChartSection | null => {
  const title = unitConverter.flowRate().titleWithoutUnit()

  const commonReturnItems = {
    title,
    fieldAssetPicker: <DropdownSelectorFlowMeter />,
    id: 'stackem-flow-meter',
  }

  if (!checkPermission() || points.length === 0 || !selectedFieldAssets.flowMeterDevice)
    return propertyLacksPermissionSectionMaker(commonReturnItems)

  const flowMeterData = data.points ?? {}

  const deviceFlowRateData =
    flowMeterData?.[selectedFieldAssets?.flowMeterDevice]?.values?.irrigationFlowRate?.[0]

  const property = fieldAssetStore.getState().properties?.[Number(selectedFieldAssets.property)]
  const timezone = property?.timezone ?? moment.tz.guess()
  const firstForecastTimestamp = +moment.tz(timezone)
  const metadata = deviceFlowRateData?.metadata ?? {}
  const onDurationMillisecond = metadata.onDurationMillisecond
  const onDurationMinutes = onDurationMillisecond ? onDurationMillisecond / 60000 : 0
  const cumulativeFlowGallons = metadata.cumulativeFlowGallons ?? 0
  const { minFlowRateGpm, maxFlowRateGpm, name } = metadata
  const convertedMinFlowRate = unitConverter.flowRate(minFlowRateGpm).valueWithNoRounding()
  const convertedMaxFlowRate = unitConverter.flowRate(maxFlowRateGpm).valueWithNoRounding()
  const defaultMinFlowRate = convertedMinFlowRate || ''
  const defaultMaxFlowRate = convertedMaxFlowRate || ''
  const isFlowRateData = deviceFlowRateData?.timeseries.some((d) => !isNil(d.value.flowRateGpm))

  const flowRateSerie: SeriesOptionsType = {
    zones: [
      {
        value: convertedMinFlowRate ?? -1,
        color: colors.red,
      },
      {
        value: convertedMaxFlowRate ?? Infinity,
        color: colors.green300,
      },
      {
        color: colors.red,
      },
    ],
    zoneAxis: 'y',
    name: translate.phrases.banyanApp('Flow Rate'),
    tooltip: {
      valueDecimals: unitConverter.flowRate().defaultNumberOfDecimalPlaces(),
      valueSuffix: ` ${unitConverter.flowRate().suffix()}`,
    },
    data: isFlowRateData
      ? (deviceFlowRateData?.timeseries ?? []).map((d) => [
          +new Date(d.timestamp),
          unitConverter.flowRate(d.value.flowRateGpm).value(),
        ])
      : [],
    type: 'line',
    showInLegend: false,
    id: 'flow-rate',
  }

  const series: SeriesOptionsType[] = []

  series.push(flowRateSerie)

  let yAxisOptions: StackedChartSection['items'][number]['chartConfig']['yAxis'] = { plotLines: [] }

  if (!isNil(metadata.minFlowRateGpm) && !isNil(metadata.maxFlowRateGpm)) {
    const { maxFlowRateFromChart, minFlowRateFromChart } = (deviceFlowRateData?.timeseries ?? []).reduce(
      (acc, currentValue) => {
        const { flowRateGpm } = currentValue.value

        if (!flowRateGpm) return acc

        if (flowRateGpm > acc.maxFlowRateFromChart) {
          acc.maxFlowRateFromChart = flowRateGpm
        }

        if (flowRateGpm < acc.minFlowRateFromChart) {
          acc.minFlowRateFromChart = flowRateGpm
        }

        return acc
      },
      {
        maxFlowRateFromChart: Number.isFinite(metadata.maxFlowRateGpm) ? metadata.maxFlowRateGpm : -Infinity,
        minFlowRateFromChart: Number.isFinite(metadata.minFlowRateGpm) ? metadata.minFlowRateGpm : Infinity,
      },
    )

    yAxisOptions = {
      softMax: unitConverter.flowRate(maxFlowRateFromChart).value() as number,
      softMin: unitConverter.flowRate(minFlowRateFromChart).value() as number,
      plotLines: [
        {
          color: colors.darkOrange,
          dashStyle: 'Dash',
          value: unitConverter.flowRate(metadata.minFlowRateGpm).value() as number,
          width: 2,
        },
        {
          color: colors.darkOrange,
          dashStyle: 'Dash',
          value: unitConverter.flowRate(metadata.maxFlowRateGpm).value() as number,
          width: 2,
        },
      ],
    }
  }

  const isDaily = metadata.aggregationInterval === 'DAILY'

  return {
    ...commonReturnItems,
    title,
    fieldAssetPicker: <DropdownSelectorFlowMeter />,
    titleLink: FlowMeterSettingsTitleLink({
      deviceId: metadata.pumpId ?? '',
      defaultMinFlowRate,
      defaultMaxFlowRate,
      handleAfterSave: (minFlowRate, maxFlowRate) => {
        const selectedPumpDevice = selectedFieldAssets.flowMeterDevice

        if (!selectedPumpDevice) return

        const valuesForPoints = flowMeterData?.[selectedPumpDevice]?.values?.irrigationFlowRate?.[0]

        if (!valuesForPoints) return

        const updated = {
          ...valuesForPoints,
          metadata: {
            ...valuesForPoints?.metadata,
            minFlowRateGpm: minFlowRate,
            maxFlowRateGpm: maxFlowRate,
          },
        }

        const path = `points.["${selectedPumpDevice}"].values.irrigationFlowRate.0`

        updateData(path, updated)
      },
      name: name ?? '',
    }),
    id: 'stackem-flow-meter',
    items: [
      {
        chartConfig: {
          tooltip: {
            xDateFormat: getXDateFormat(isDaily),
            formatter: function (this: TooltipFormatterContextObject) {
              const { x, y } = this.points?.[0]?.point ?? {}
              const currentTimeStamp = moment.tz(x, timezone)

              const currentTimeStampString = isDaily
                ? translate.dates.format(moment.tz(currentTimeStamp, timezone), 'ddd, MMM D, YYYY')
                : translate.dates.format(moment.tz(currentTimeStamp, timezone), 'ddd, MMM D, YYYY h:mm a (z)')

              let color = colors.green300

              if (metadata.minFlowRateGpm && metadata.maxFlowRateGpm && !isNil(y)) {
                const convertedMinFlowRate = unitConverter.flowRate(metadata.minFlowRateGpm).value() as number
                const convertedMaxFlowRate = unitConverter.flowRate(metadata.maxFlowRateGpm).value() as number

                if (y < convertedMinFlowRate || y > convertedMaxFlowRate) {
                  color = colors.red
                }
              }

              return `
                <strong>${currentTimeStampString}</strong>
                <table style="margin-top: 5px; width: 100%;">
                  <tr>
                    <td>
                      <div style="background:${color}; min-width: 14px; min-height: 5px; margin: 0 7px 0 0;"></div>
                    </td>
                    <td style="text-align: left"><div style="width:max-content">${translate.phrases.banyanApp(
                      'Flow Rate',
                    )}:</div></td>
                    <td style="text-align: right; padding: 0 0 0 5px;">
                      <div style="display: flex; justify-content: flex-start;">
                        <b><span id="flow-rate:valueOpening"></span>${y}<span id="flow-rate:valueClosing"></span> <span id="flow-rate:valueSuffixOpening"></span> ${unitConverter
                .flowRate()
                .suffix()}<span id="flow-rate:valueSuffixClosing"></span></b>
                      </div>
                    </td>
                  </tr>
                </table>`
            },
          },
          semiosHighchartsAdditions: {
            id: 'flow-meter',
            firstForecastTimestamp,
          },
          chart: {
            type: 'line',
          },
          yAxis: {
            minorTickInterval: 'auto',
            ...yAxisOptions,
          },
          series,
        },
        childrenLower: (
          <div
            css={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <FlowMeterChartLegend
              maxFlowRateGpm={maxFlowRateGpm || null}
              minFlowRateGpm={minFlowRateGpm || null}
            />
            <div
              css={{
                textAlign: 'center',
                fontSize: '14px',
                color: colors.grey900,
                margin: '10px 10px 15px',
                display: 'flex',
              }}
            >
              <div>
                {translate.phrases.templates('{{label}}: {{value}}', {
                  label: translate.phrases.banyanApp('On Duration'),
                  value: minutesToHoursAndMinutes(onDurationMinutes),
                })}
              </div>
              <div css={{ padding: '0 10px' }}>{'|'}</div>
              <div>
                {translate.phrases.templates('{{label}}: {{value}}', {
                  label: translate.phrases.banyanApp('Cumulative Flow'),
                  value: unitConverter.flowMeter(cumulativeFlowGallons).valueWithSuffix(),
                })}
              </div>
            </div>
          </div>
        ),
      },
    ],
  }
}
